import { observable } from '@legendapp/state';
import { enableReactTracking } from '@legendapp/state/config/enableReactTracking';
import { enableReactUse } from '@legendapp/state/config/enableReactUse';
import { QueryClient } from '@tanstack/query-core';
import { logger } from '@/lib/utils/logger';
import { ConversationResource, FormAnswer } from '@/types';
import { ConversationAdvice } from '@/types/api/conversation.types';
import { adviceService } from '@/lib/services';
import { Session } from 'next-auth';
import { batch } from '@legendapp/state';
import { getSession } from 'next-auth/react';
import { sidebarActions } from './sidebar.store';
import { API_CONFIG } from '@/lib/config/api.config';

// Enable React integration with auto tracking
enableReactTracking({
  auto: true,
});

// Enable React use for better hooks integration
enableReactUse();

/**
 * Query client configuration for managing API cache
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

/**
 * Request status types for advice state
 */
export type RequestStatus = 'ready' | 'submitted' | 'streaming' | 'error';

/**
 * Interface representing the advice store state
 */
export interface AdviceState {
  status: RequestStatus;
  langFlowEnabled: boolean;
  error: Error | null;
  activeController: AbortController | null;
  streamingAdviceId: string | null;
  isChatLoading: boolean;
  isStreaming: boolean;
  isTransitioning: boolean;
  currentChatId: string | null;
  previousChatId: string | null;
  conversations: { items: ConversationResource[] };
  activeConversation: ConversationResource | null;
  advices: ConversationAdvice[];
  session: Session | null;
  isFirstAdviceAnimationComplete: boolean;
}

/**
 * Initial state for the advice store
 */
const initialState: AdviceState = {
  status: 'ready',
  langFlowEnabled: false,
  error: null,
  activeController: null,
  streamingAdviceId: null,
  isChatLoading: false,
  isStreaming: false,
  isTransitioning: false,
  currentChatId: null,
  previousChatId: null,
  conversations: { items: [] },
  activeConversation: null,
  advices: [],
  session: null,
  isFirstAdviceAnimationComplete: false,
};

// Create the observable store
export const adviceState = observable<AdviceState>(initialState);

/**
 * Actions for managing advice state
 */
export const adviceActions = {
  /**
   * Sets the current session
   */
  setSession: (session: Session | null) => {
    adviceState.session.set(session);
  },

  /**
   * Ensures a valid session exists
   */
  ensureSession: async (): Promise<Session | null> => {
    let session = adviceState.session.get();
    if (!session) {
      const session = await getSession();
      if (session) {
        adviceActions.setSession(session);
      }
    }
    if (!session) {
      throw new Error('Unauthorized: no session found.');
    }
    return session;
  },

  /**
   * Starts a new request
   */
  startRequest: (isStreaming: boolean = false) => {
    batch(() => {
      adviceState.status.set('submitted');
      adviceState.error.set(null);
      adviceState.isChatLoading.set(!isStreaming);
      adviceState.isStreaming.set(false);
    });
  },

  /**
   * Starts loading state
   */
  startLoading: () => {
    batch(() => {
      adviceState.status.set('streaming');
      adviceState.error.set(null);
      adviceState.isChatLoading.set(true);
      adviceState.isStreaming.set(false);
    });
  },

  /**
   * Starts streaming state
   */
  startStreaming: (adviceId?: string) => {
    batch(() => {
      adviceState.status.set('streaming');
      adviceState.error.set(null);
      adviceState.isStreaming.set(true);

      if (adviceId) {
        adviceState.streamingAdviceId.set(adviceId);
        const currentAdvices = adviceState.advices.get();
        const adviceIndex = currentAdvices.findIndex(
          (a: ConversationAdvice) => a.id === adviceId
        );

        if (adviceIndex !== -1) {
          const updatedAdvices = [...currentAdvices];
          updatedAdvices[adviceIndex] = {
            ...updatedAdvices[adviceIndex],
            status: 'SUBMITTED',
          };

          adviceState.advices.set(updatedAdvices);

          const currentConversation = adviceState.activeConversation.get();
          if (currentConversation) {
            const updatedConversation = {
              ...currentConversation,
              advices: updatedAdvices,
            };
            adviceState.activeConversation.set(updatedConversation);
          }
        }
      }
    });
  },

  /**
   * Completes streaming state
   */
  completeStream: () => {
    adviceState.status.set('submitted');
    adviceState.error.set(null);
  },

  /**
   * Sets error state
   */
  setError: (error: Error) => {
    batch(() => {
      adviceState.status.set('error');
      adviceState.error.set(error);
      adviceState.isChatLoading.set(false);
      adviceState.isStreaming.set(false);
    });
  },

  /**
   * Completes current request
   */
  completeRequest: () => {
    const streamingAdviceId = adviceState.streamingAdviceId.get();
    let idToComplete = streamingAdviceId;

    if (!idToComplete) {
      const currentAdvices = adviceState.advices.get();
      if (currentAdvices.length > 0) {
        idToComplete = currentAdvices[currentAdvices.length - 1].id;
      }
    }

    if (idToComplete) {
      const currentAdvices = adviceState.advices.get();
      const adviceIndex = currentAdvices.findIndex(
        (a: ConversationAdvice) => a.id === idToComplete
      );

      if (adviceIndex !== -1) {
        const updatedAdvices = [...currentAdvices];
        updatedAdvices[adviceIndex] = {
          ...updatedAdvices[adviceIndex],
          status: 'COMPLETED',
          isStreaming: false,
          advice:
            updatedAdvices[adviceIndex].advice &&
            updatedAdvices[adviceIndex].advice.trim().length > 5
              ? updatedAdvices[adviceIndex].advice
              : '',
        };

        batch(() => {
          adviceState.advices.set(updatedAdvices);
          const currentConversation = adviceState.activeConversation.get();
          if (currentConversation) {
            const updatedConversation = {
              ...currentConversation,
              advices: updatedAdvices,
            };
            adviceState.activeConversation.set(updatedConversation);
          }
        });
      }
    }

    batch(() => {
      adviceState.status.set('ready');
      adviceState.isStreaming.set(false);
      adviceState.streamingAdviceId.set(null);
      adviceState.isChatLoading.set(false);
    });
  },

  /**
   * Handles request failure
   */
  failRequest: (error: Error | string) => {
    if (error === 'RESET_CHAT') {
      const controller = adviceState.activeController.get();
      if (controller instanceof AbortController) {
        controller.abort('RESET_CHAT');
        adviceActions.setActiveController(null);
      }
      adviceActions.reset();
    } else {
      adviceState.status.set('error');
      adviceState.error.set(error as Error);
    }
  },

  /**
   * Sets active controller
   */
  setActiveController: (controller: AbortController | null) => {
    adviceState.activeController.set(controller);
  },

  /**
   * Starts transition state
   */
  startTransition: () => {
    adviceState.isTransitioning.set(true);
    adviceState.status.set('submitted');
  },

  /**
   * Completes transition state
   */
  completeTransition: () => {
    adviceState.isTransitioning.set(false);
    adviceState.status.set('ready');
  },

  /**
   * Sets current chat ID
   */
  setCurrentChatId: (chatId: string | null) => {
    const prevId = adviceState.currentChatId.get();
    adviceState.currentChatId.set(chatId);
    adviceState.previousChatId.set(prevId);
  },

  /**
   * Fetches conversations list
   */
  fetchConversations: async () => {
    let session: Session | null;
    try {
      session = await adviceActions.ensureSession();
      const data = await adviceService.list(session as Session);
      const mappedItems = (data?.items || []).map((item) => ({
        id: item.id,
        title: item.title,
        createdAt: new Date(item.createdAt),
      }));

      const response = { items: mappedItems };
      adviceState.conversations.set(
        response as unknown as { items: ConversationResource[] }
      );
      queryClient.setQueryData(['conversations'], response);
      return response;
    } catch (error) {
      logger.error('[adviceStore] Failed to fetch conversations:', error);
      return { items: [] };
    }
  },

  /**
   * Transforms API sources array into flat properties for backward compatibility
   */
  transformSources: (advice: ConversationAdvice): ConversationAdvice => {
    if (
      !advice ||
      !Array.isArray(advice.sources) ||
      advice.sources.length === 0
    ) {
      return advice;
    }

    const result = { ...advice };

    // Initialize arrays
    result.urls = result.urls || [];
    result.page_source = result.page_source || [];
    result.page_title = result.page_title || [];
    result.urls_ccq = result.urls_ccq || [];
    result.page_source_ccq = result.page_source_ccq || [];
    result.page_title_ccq = result.page_title_ccq || [];
    result.texts_ccq = result.texts_ccq || [];
    result.modifications_ccq = result.modifications_ccq || [];
    result.urls_ccq_modif = result.urls_ccq_modif || [];
    result.page_source_ccq_modif = result.page_source_ccq_modif || [];
    result.page_title_ccq_modif = result.page_title_ccq_modif || [];
    result.texts_ccq_modif = result.texts_ccq_modif || [];
    result.caselaw_reference = result.caselaw_reference || [];
    result.caselaw_titre = result.caselaw_titre || [];
    result.caselaw_lienURL = result.caselaw_lienURL || [];
    result.caselaw_nomsDesPartis = result.caselaw_nomsDesPartis || [];

    // Initialize relevant_forms if needed
    if (!result.relevant_forms) {
      result.relevant_forms = {
        page_url: [],
        doc_url: [],
        filename: [],
        title: [],
        content: [],
        filled_by: [],
      };
    }

    // Process each source based on its type
    advice.sources.forEach((source: any) => {
      switch (source.type) {
        case 'INFO':
          if (source.url && !result.urls!.includes(source.url)) {
            result.urls!.push(source.url);
            result.page_source!.push(source.pageSource || '');
            result.page_title!.push(source.pageTitle || '');
          }
          break;
        case 'LAW':
          const lawExists =
            result.urls_ccq!.findIndex(
              (url, index) =>
                url === source.url &&
                result.page_title_ccq![index] ===
                  (source.pageTitle || source.title)
            ) !== -1;

          if (source.url && !lawExists) {
            result.urls_ccq!.push(source.url);
            result.page_source_ccq!.push(source.pageSource || '');
            result.page_title_ccq!.push(source.pageTitle || '');
            result.texts_ccq!.push(source.content || '');

            // Initialize as boolean[] if not already an array
            if (!Array.isArray(result.modifications_ccq)) {
              result.modifications_ccq = [] as boolean[];
            }
            (result.modifications_ccq as boolean[]).push(
              source.isModified || false
            );
            // We don't add to texts_ccq_modif here - modified texts should come from MODIFIED_LAW sources
          }
          break;
        case 'MODIFIED_LAW':
          const modLawExists =
            result.urls_ccq_modif!.findIndex(
              (url, index) =>
                url === source.url &&
                result.page_title_ccq_modif![index] ===
                  (source.pageTitle || source.title)
            ) !== -1;
          if (source.url && !modLawExists) {
            result.urls_ccq_modif!.push(source.url);
            result.page_source_ccq_modif!.push(source.pageSource || '');
            result.page_title_ccq_modif!.push(source.pageTitle || '');
            result.texts_ccq_modif!.push(source.content || '');
            // We don't add to texts_ccq_modif here - modified texts should come from MODIFIED_LAW sources
          }
          break;
        case 'CASELAW':
          if (
            source.reference &&
            !result.caselaw_reference!.includes(source.reference)
          ) {
            result.caselaw_reference!.push(source.reference);
            result.caselaw_titre!.push(source.title || '');
            result.caselaw_lienURL!.push(source.url || '');
            result.caselaw_nomsDesPartis!.push(source.nomsDesPartis || '');
          }
          break;
        case 'FORM':
          // Only add to forms if not already present
          if (
            source.docUrl &&
            !result.relevant_forms!.doc_url.includes(source.docUrl)
          ) {
            result.relevant_forms!.page_url.push(source.pageUrl || '');
            result.relevant_forms!.doc_url.push(source.docUrl || '');
            result.relevant_forms!.filename.push(source.filename || '');
            result.relevant_forms!.title.push(source.title || '');
            result.relevant_forms!.content.push(source.content || '');
            result.relevant_forms!.filled_by.push(source.filledBy || []);
          }
          break;
      }
    });

    return result;
  },

  /**
   * Sets active conversation
   */
  setActiveConversation: (conversation: ConversationResource | null) => {
    if (conversation) {
      // Transform each advice to include flat arrays from sources
      if (conversation.advices && Array.isArray(conversation.advices)) {
        conversation.advices = conversation.advices.map((advice) =>
          adviceActions.transformSources(advice)
        );
      }

      queryClient.setQueryData(['conversation', conversation.id], conversation);
    }
    adviceState.activeConversation.set(conversation);
    adviceState.advices.set(conversation?.advices || []);
    adviceState.isFirstAdviceAnimationComplete.set(false);
  },

  /**
   * Adds new advice
   */
  addAdvice: (advice: ConversationAdvice) => {
    const currentConversation = adviceState.activeConversation.get();
    if (!currentConversation) return;

    const adviceExists = currentConversation.advices.some(
      (a) => a.id === advice.id
    );

    if (adviceExists) {
      adviceActions.updateAdvice(advice.id, advice);
      return;
    }

    const updatedConversation = {
      ...currentConversation,
      advices: [...currentConversation.advices, advice],
    };

    queryClient.setQueryData(
      ['conversation', currentConversation.id],
      updatedConversation
    );

    adviceState.activeConversation.set(updatedConversation);
    adviceState.advices.set(updatedConversation.advices);
  },

  /**
   * Updates existing advice
   */
  updateAdvice: async (
    adviceId: string,
    updatedAdvice: Partial<ConversationAdvice>
  ) => {
    const current = adviceState.advices.get();
    const index = current.findIndex(
      (a: ConversationAdvice) => a.id === adviceId
    );

    if (index !== -1) {
      const existingAdvice = current[index];
      const updatedAdvices = [...current];

      // Transform sources into flat arrays if available
      let processedAdvice = { ...updatedAdvice };
      if (updatedAdvice.sources) {
        processedAdvice = adviceActions.transformSources({
          ...existingAdvice,
          ...updatedAdvice,
        });
      }

      // For error responses, ensure we use the direct error message
      if (processedAdvice.status === 'ERROR' || processedAdvice.isError) {
        updatedAdvices[index] = {
          ...existingAdvice,
          ...processedAdvice,
          // Make sure error flag is set
          isError: true,
        };
      }
      // For normal updates, just merge the changes
      else {
        updatedAdvices[index] = { ...existingAdvice, ...processedAdvice };

        // Extra check to ensure relevant_forms is properly merged
        if (
          processedAdvice.relevant_forms &&
          !updatedAdvices[index].relevant_forms
        ) {
          console.log(
            '[AdviceStore:updateAdvice] Adding relevant_forms to advice:',
            {
              adviceId,
              relevant_forms: processedAdvice.relevant_forms,
            }
          );
          updatedAdvices[index].relevant_forms = processedAdvice.relevant_forms;
        }
      }

      // Update advices state
      adviceState.advices.set(updatedAdvices);

      // IMPORTANT: Also update the activeConversation to keep states in sync
      const currentConversation = adviceState.activeConversation.get();
      if (currentConversation) {
        const updatedConversation = {
          ...currentConversation,
          advices: updatedAdvices,
        };
        adviceState.activeConversation.set(updatedConversation);
      }

      // Call the API to persist the update
      const session = await adviceActions.ensureSession();
      if (session) {
        await adviceService.updateAdvice(adviceId, updatedAdvice, session);
      }
    }
  },

  /**
   * Deletes chat
   */
  deleteChat: async (chatId: string) => {
    const session = await adviceActions.ensureSession();

    const prevConvs = adviceState.conversations.get();
    const updatedItems = prevConvs.items.filter((item) => item.id !== chatId);
    adviceState.conversations.set({ items: updatedItems });

    try {
      sidebarActions.startDelete();
      sidebarActions.resetSidebarState();

      queryClient.setQueryData<{ items: ConversationResource[] }>(
        ['conversations'],
        (old) => {
          if (!old) return { items: [] };
          return {
            items: old.items.filter((item) => item.id !== chatId),
          };
        }
      );

      await adviceService.delete(chatId, session as Session);

      queryClient.removeQueries({ queryKey: ['conversation', chatId] });

      await queryClient.invalidateQueries({
        queryKey: ['conversations'],
        exact: true,
        refetchType: 'all',
      });

      batch(() => {
        if (chatId === adviceState.currentChatId.get()) {
          adviceState.currentChatId.set(null);
          adviceState.activeConversation.set(null);
          adviceState.advices.set([]);
        }
      });

      sidebarActions.completeDelete();

      return { items: updatedItems };
    } catch (error) {
      logger.error('[adviceStore] Failed to delete chat:', error);

      if (prevConvs) {
        queryClient.setQueryData(['conversations'], prevConvs);
      }

      adviceState.conversations.set(prevConvs);
      sidebarActions.setError(error as Error);
      throw error;
    }
  },

  /**
   * Creates new conversation
   */
  createConversation: async (title: string, formAnswerId: string) => {
    const session = await adviceActions.ensureSession();

    try {
      const newConversation = await adviceService.create(
        {
          title,
          description: '',
          formAnswerId: formAnswerId,
        },
        session as Session
      );
      const conversationId = newConversation.id;

      const optimisticAdvice: ConversationAdvice = {
        id: conversationId,
        question: title,
        advice: '',
        answer: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'SUBMITTED' as const,
        urls: [],
        page_source: [],
        page_title: [],
        urls_ccq: [],
        page_source_ccq: [],
        page_title_ccq: [],
        texts_ccq: [],
        modifications_ccq: [],
        urls_ccq_modif: [],
        page_source_ccq_modif: [],
        page_title_ccq_modif: [],
        texts_ccq_modif: [],
        caselaw_reference: [],
        caselaw_titre: [],
        caselaw_lienURL: [],
        caselaw_nomsDesPartis: [],
        isNew: true,
      };

      const newItem = {
        id: conversationId,
        title,
        threadId: conversationId,
        likes: 0,
        dislikes: 0,
        createdAt: new Date(),
        advices: [optimisticAdvice],
      };

      batch(() => {
        queryClient.setQueryData(['conversations'], (old: any) => {
          if (!old?.items) {
            return { items: [newItem] };
          }
          return {
            items: [newItem, ...old.items],
          };
        });

        const currentConversations = adviceState.conversations.get();
        adviceState.conversations.set({
          items: [newItem, ...(currentConversations.items || [])],
        });

        const activeConversation = {
          ...newConversation,
          threadId: newConversation.id,
          likes: 0,
          dislikes: 0,
          advices: [optimisticAdvice],
          createdAt: new Date(newConversation.createdAt),
        };

        adviceActions.setCurrentChatId(conversationId);
        adviceActions.setActiveConversation(activeConversation);
      });

      queryClient.invalidateQueries({
        queryKey: ['conversations'],
        refetchType: 'all',
      });

      return newConversation;
    } catch (error) {
      logger.error('[adviceStore] Failed to create conversation:', error);
      throw error;
    }
  },

  /**
   * Handles streaming chunk
   */
  handleChunk: async (payload: any) => {
    const { advices } = adviceState.get();
    const updatedAdvices = [...advices];
    const adviceIndex = updatedAdvices.findIndex(
      (a) => a.id === payload.requestId
    );

    if (adviceIndex !== -1) {
      // Use structuredClone for a deep copy to avoid mutation issues
      const currentAdvice = structuredClone(updatedAdvices[adviceIndex]);
      let contentPart: string | undefined = undefined;

      // Ensure sources array exists
      if (!Array.isArray(currentAdvice.sources)) {
        currentAdvice.sources = [];
      }

      // REVISED upsertSource logic - Align with advice.service.ts
      const upsertSource = (newSource: any) => {
        if (!newSource || !newSource.type) return;

        if (!Array.isArray(currentAdvice.sources)) {
          currentAdvice.sources = []; // Ensure it exists
        }

        let uniqueKey: string | null = null;
        switch (newSource.type) {
          case 'INFO':
          case 'FORM': // Keep using URL/docUrl/pageUrl for INFO and FORM
            uniqueKey = newSource.url || newSource.docUrl || newSource.pageUrl;
            break;
          case 'LAW':
          case 'MODIFIED_LAW':
            // Use type + URL + title/pageTitle for Laws
            uniqueKey = `${newSource.type}-${newSource.url}-${newSource.pageTitle || newSource.title}`;
            break;
          case 'CASELAW':
            uniqueKey = newSource.reference;
            break;
        }

        if (!uniqueKey) return; // Cannot add source without a unique key

        const existingIndex = currentAdvice.sources.findIndex((s: any) => {
          let existingKey: string | null = null;
          switch (s.type) {
            case 'INFO':
            case 'FORM':
              existingKey = s.url || s.docUrl || s.pageUrl;
              break;
            case 'LAW':
            case 'MODIFIED_LAW':
              // Construct the same composite key for comparison
              existingKey = `${s.type}-${s.url}-${s.pageTitle || s.title}`;
              break;
            case 'CASELAW':
              existingKey = s.reference;
              break;
            default:
              return false; // Unknown type
          }
          // Compare the constructed/retrieved keys
          return existingKey !== null && existingKey === uniqueKey;
        });

        if (existingIndex === -1) {
          currentAdvice.sources.push(newSource);
        } else {
          // Optionally update existing source
          currentAdvice.sources[existingIndex] = {
            ...currentAdvice.sources[existingIndex],
            ...newSource,
          };
        }
      };

      // Always set isStreaming to true for any non-final chunk
      if (!payload.isFinalChunk) {
        currentAdvice.isStreaming = true;
        // Ensure the status is set to streaming as well
        currentAdvice.status = 'STREAMING';
      }

      // --- Process Payload Data ---

      // **MODIFICATION START**: Prioritize structured 'sources' array
      // Only process if payload.response.sources is a non-empty array
      if (
        payload.response?.sources &&
        Array.isArray(payload.response.sources) &&
        payload.response.sources.length > 0
      ) {
        logger.debug(
          '[AdviceStore:handleChunk] Processing sources array from payload',
          {
            adviceId: payload.requestId,
            count: payload.response.sources.length,
            types: [
              ...new Set(payload.response.sources.map((s: any) => s.type)),
            ],
          }
        );
        payload.response.sources.forEach(upsertSource);
        // Get advice text if available within the same response structure
        contentPart = payload.response?.advice;
      }
      // **REMOVED**: Fallback logic for legacy flat arrays is no longer needed.
      // else {
      //    ... legacy processing removed ...
      // }

      // Get advice text if it came separately or as a plain string
      if (contentPart === undefined) {
        // Only check if not set via sources processing
        if (
          payload.response?.advice &&
          typeof payload.response.advice === 'string'
        ) {
          contentPart = payload.response.advice;
        } else if (typeof payload.response === 'string') {
          // Handle cases where the response might be just the advice string initially
          contentPart = payload.response; // Assume it's advice text if it's a string
        }
      }

      // Update advice text content
      if (contentPart !== undefined) {
        // Use cleanStreamingText for non-final chunks if needed, finalize on final chunk
        // Ensure we only update if contentPart is a non-empty string
        if (typeof contentPart === 'string' && contentPart.trim().length > 0) {
          currentAdvice.advice = payload.isFinalChunk
            ? finalizeMarkdown(contentPart)
            : contentPart; // Append or replace based on streaming logic
        }
      }

      // --- Final Chunk Processing ---
      if (payload.isFinalChunk) {
        currentAdvice.isStreaming = false;
        currentAdvice.status = 'COMPLETED';

        // Ensure advice text is finalized
        currentAdvice.advice = finalizeMarkdown(currentAdvice.advice || '');

        // Log the final sources array before saving
        logger.info('[AdviceStore] Final sources array before saving:', {
          adviceId: currentAdvice.id,
          count: currentAdvice.sources.length,
          types: [...new Set(currentAdvice.sources.map((s: any) => s.type))],
        });

        // Update the store immediately for UI
        updatedAdvices[adviceIndex] = currentAdvice;
        batch(() => {
          // Batch state updates
          adviceState.advices.set(updatedAdvices);
          adviceState.isStreaming.set(false); // Ensure streaming flag is off
        });

        // --- Save to Backend ---
        try {
          logger.info(
            '[AdviceStore] Updating advice with final response via service',
            { adviceId: currentAdvice.id }
          );
          const currentSession =
            adviceState.session.get() || (await adviceActions.ensureSession()); // Use existing session if available
          if (currentSession) {
            // **SIMPLIFIED PAYLOAD**
            const servicePayload: Partial<ConversationAdvice> = {
              advice: currentAdvice.advice,
              question: currentAdvice.question, // Ensure question is included
              sources: currentAdvice.sources, // Send the definitive sources array processed by handleChunk
              // Include relevant_forms if it exists on the advice object
              ...(currentAdvice.relevant_forms && {
                relevant_forms: currentAdvice.relevant_forms,
              }),
            };

            // Log the payload being sent to the service
            logger.debug(
              '[AdviceStore:handleChunk Final Save] Payload sent to adviceService.updateAdvice:',
              servicePayload
            );

            /*
            // [COMMENTED OUT] - Avoid redundant update call from handleChunk
            // The final update should happen once in submitMessage after getAdvice returns.
            await adviceService.updateAdvice(
              currentAdvice.id,
              servicePayload, // Pass the clean payload with prioritized sources array
              currentSession
            );
            */
            // logger.info('[AdviceStore] Successfully called adviceService.updateAdvice', { adviceId: currentAdvice.id }); // Comment out log as well
          } else {
            logger.error(
              '[AdviceStore] Unable to update advice: No valid session'
            );
          }
        } catch (error) {
          logger.error(
            '[AdviceStore] Error calling adviceService.updateAdvice:',
            { adviceId: currentAdvice.id, error }
          );
        }

        // Final state updates after saving attempt
        adviceActions.completeRequest(); // Correct: Call action via adviceActions
      } else {
        // Intermediate chunk: Update UI optimistically
        updatedAdvices[adviceIndex] = currentAdvice;
        batch(() => {
          // Batch state updates
          adviceState.advices.set(updatedAdvices);
          adviceState.isStreaming.set(true); // Ensure streaming flag is on
        });
      }
    } // End if (adviceIndex !== -1)
  },

  getPrompt: async ({
    chatId,
    adviceId,
    message,
    lang,
    formAnswer,
    conversationHistory,
    abortSignal,
  }: {
    chatId: string;
    adviceId: string;
    message: string;
    lang: string;
    formAnswer: FormAnswer;
    conversationHistory: [];
    abortSignal?: AbortSignal;
  }) => {
    try {
      const session = await adviceActions.ensureSession();

      const response = await adviceService.getLambdaPrompt(
        chatId,
        adviceId,
        message,
        lang,
        formAnswer,
        conversationHistory,
        session as Session,
        abortSignal
      );
      return response;
    } catch (error) {
      if (error && error === 'RESET_CHAT') {
        console.log('[adviceStore:getPrompt] Reset chat');
        return 'RESET_CHAT';
      }
      console.error('[adviceStore:getPrompt] Error:', error);
      return null;
    }
  },

  submitMessage: async (
    message: string,
    chatId: string | null,
    lang: string,
    formAnswer: FormAnswer
  ) => {
    logger.debug('[adviceStore:submitMessage] Starting message submission', {
      chatId,
      messageLength: message.length,
    });

    const session = await adviceActions.ensureSession();

    try {
      batch(() => {
        adviceActions.startRequest(true);
        adviceState.status.set('submitted');
        adviceState.error.set(null);
      });

      // Cancel any existing request
      const controller = adviceState.activeController.get();
      const streamingAdviceId = adviceState.streamingAdviceId.get(); // Get the ID before clearing
      if (controller instanceof AbortController) {
        controller.abort(new Error('Operation cancelled by user'));
        // If an advice was streaming when aborted, update its status
        if (streamingAdviceId && session) {
          try {
            await adviceService.updateAdviceItemStatus(
              streamingAdviceId,
              'ABORTED',
              session as Session
            );
          } catch (err) {
            logger.error(
              '[adviceStore:submitMessage] Failed to update status to aborted',
              err
            );
          }
        }
        adviceActions.setActiveController(null);
      }

      // Create new controller for this request
      const newController = new AbortController();
      adviceActions.setActiveController(newController);

      // Create new conversation if none exists
      let currentConv = adviceState.activeConversation.get();
      if (!currentConv || !chatId) {
        const newConversation = {
          id: `temp-${Date.now()}`,
          title:
            message.length > 60 ? `${message.substring(0, 57)}...` : message,
          threadId: `temp-${Date.now()}`,
          likes: 0,
          dislikes: 0,
          createdAt: new Date().toISOString(),
          advices: [],
        };

        currentConv = newConversation as unknown as ConversationResource;
        chatId = newConversation.id;

        batch(() => {
          adviceState.activeConversation.set(currentConv);
          adviceState.currentChatId.set(chatId);
        });
      }

      // Create and store the advice
      console.log('[adviceStore:submitMessage] Creating advice', {
        chatId,
        message,
        session: session as Session,
      });
      const newAdvice = await adviceService.createAdvice(
        chatId,
        message,
        session as Session
      );

      if (!newAdvice || !newAdvice.id) {
        adviceActions.failRequest('Failed to create advice on server');
        return;
      }

      // Create a single advice entry with loading state
      const loadingAdvice: ConversationAdvice = {
        id: newAdvice.id,
        question: message,
        advice: '',
        status: 'SUBMITTED' as const,
        answer: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        urls: [],
        page_source: [],
        page_title: [],
        urls_ccq: [],
        page_source_ccq: [],
        page_title_ccq: [],
        texts_ccq: [],
        modifications_ccq: [],
        urls_ccq_modif: [],
        page_source_ccq_modif: [],
        page_title_ccq_modif: [],
        texts_ccq_modif: [],
        caselaw_reference: [],
        caselaw_titre: [],
        caselaw_lienURL: [],
        caselaw_nomsDesPartis: [],
        relevant_forms: {
          page_url: [],
          doc_url: [],
          filename: [],
          title: [],
          content: [],
          filled_by: [],
        },
        isNew: true,
      };

      const isNewConversation = !currentConv.advices.length;
      const updatedAdvices = isNewConversation
        ? [loadingAdvice]
        : [...currentConv.advices, loadingAdvice];

      const updatedConversation = {
        ...currentConv,
        advices: updatedAdvices,
      };

      batch(() => {
        adviceState.activeConversation.set(updatedConversation);
        adviceState.advices.set(updatedAdvices);
        adviceActions.startStreaming(newAdvice.id);
      });

      // Check if streaming is enabled
      const langFlowEnabled = adviceState.langFlowEnabled.get();

      /**
       * Handles incoming chunks of streaming data
       * @param chunk The chunk of data received from the stream
       * @returns void or null if no chatId exists
       */
      const onChunk = (chunk: any) => {
        return chatId ? adviceActions.handleChunk(chunk) : null;
      };

      let response;
      console.log(
        '[adviceStore:submitMessage] isNewConversation:',
        isNewConversation
      );

      const _t0 = performance.now();
      const conversationHistory = !isNewConversation
        ? await adviceService.getConversationHistory(chatId, session as Session)
        : [];
      console.log(`[TIMING] Conversation history fetch: ${(performance.now() - _t0).toFixed(0)}ms`);

      console.log(
        '[adviceStore:submitMessage] Conversation history:',
        conversationHistory
      );
      conversationHistory;

      const _t1 = performance.now();
      const promptResponse = await adviceActions.getPrompt({
        chatId,
        adviceId: newAdvice.id,
        message,
        lang,
        formAnswer,
        conversationHistory: conversationHistory,
        abortSignal: newController.signal,
      });
      console.log(`[TIMING] Lambda prompt API: ${(performance.now() - _t1).toFixed(0)}ms`);

      console.log('[adviceStore:submitMessage] Lambda prompt response:', {
        response: promptResponse,
      });

      console.log('[adviceStore:submitMessage] Prompt:', promptResponse);

      if (promptResponse.status === 'blocked') {
        console.log(
          '[adviceStore:submitMessage] Lambda returned blocked status'
        );

        if (session) {
          try {
            await adviceService.updateAdviceItemStatus(
              newAdvice.id,
              'BLOCKED',
              session as Session
            );
          } catch (err) {
            logger.error(
              '[adviceStore:submitMessage] Failed to update status to blocked',
              err
            );
          }
        }

        // Create blocked advice object
        const blockedAdviceData: Partial<ConversationAdvice> = {
          status: 'BLOCKED',
          advice:
            promptResponse?.response ||
            'Desolé, je ne peux pas répondre à cette question.',
          isStreaming: false,
          isError: false,
        };

        // Update the advice using our updateAdvice action
        await adviceActions.updateAdvice(newAdvice.id, blockedAdviceData);

        // Update the conversation in a single batch update
        batch(() => {
          const currentAdvices = adviceState.advices.get();
          const finalConversation = {
            ...adviceState.activeConversation.get(),
            advices: currentAdvices,
          };

          adviceState.activeConversation.set(
            finalConversation as unknown as ConversationResource
          );
          adviceState.status.set('ready');
          adviceState.isStreaming.set(false);
          adviceState.isChatLoading.set(false);
          adviceState.streamingAdviceId.set(null);
        });

        // Clean up and update cache
        adviceActions.setActiveController(null);
        queryClient.invalidateQueries({
          queryKey: ['conversations'],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: ['conversation', chatId],
          exact: true,
        });

        return; // Return early, do not proceed to Bedrock streaming
      }

      // Update local state with sources from Lambda response.
      // Lambda handles citation numbering and prompt augmentation — pass prompt as-is to Bedrock.
      const _t2 = performance.now();
      const augmentedPrompt = promptResponse.response; // Lambda prompt already includes citation instructions

      if (
        promptResponse &&
        promptResponse.sources &&
        Array.isArray(promptResponse.sources) &&
        promptResponse.status !== 'error'
      ) {
        // Sources already have citationIndex from Lambda (merged in processLambdaResponse)
        const sourcesToStore = promptResponse.sources;

        const currentAdvices = adviceState.advices.get();
        const adviceIndex = currentAdvices.findIndex(
          (a) => a.id === newAdvice.id
        );
        if (adviceIndex !== -1) {
          const updatedAdvicesWithSources = [...currentAdvices];
          updatedAdvicesWithSources[adviceIndex] = structuredClone({
            ...updatedAdvicesWithSources[adviceIndex],
            sources: sourcesToStore,
          });
          adviceState.advices.set(updatedAdvicesWithSources);
          logger.debug(
            '[adviceStore:submitMessage] Updated local advice with sources from prompt response',
            {
              adviceId: newAdvice.id,
              sourceCount: sourcesToStore.length,
              citedCount: sourcesToStore.filter((s: any) => s.citationIndex != null).length,
            }
          );
        } else {
          logger.error(
            '[adviceStore:submitMessage] Could not find advice to update with prompt sources',
            { adviceId: newAdvice.id }
          );
        }
      } else if (
        promptResponse &&
        promptResponse !== 'RESET_CHAT' &&
        promptResponse.status !== 'error'
      ) {
        logger.warn(
          '[adviceStore:submitMessage] Prompt response received but did not contain a valid sources array',
          { promptResponse }
        );
      }
      console.log(`[TIMING] Citation processing + state update: ${(performance.now() - _t2).toFixed(0)}ms`);
      console.log(`[TIMING] Augmented prompt length: ${augmentedPrompt?.length || 0} chars`);



      if (promptResponse === 'RESET_CHAT') {
        console.log('[adviceStore:submitMessage] No prompt response');
        await adviceService.updateAdviceItemStatus(
          newAdvice.id,
          'ABORTED',
          session as Session
        );
        adviceActions.reset();
        return;
      }

      if (
        promptResponse.status === 'error' ||
        promptResponse.adviceId === 'NA'
      ) {
        console.log('[adviceStore:submitMessage] Lambda returned error status');

        // Clear session storage to prevent flashing - Defer execution to avoid blocking
        // /* // [PERF_TEST] Commented out to test impact on click handler duration
        setTimeout(() => {
          try {
            if (typeof window !== 'undefined') {
              // Clear session storage by specific keys
              sessionStorage.removeItem(`last-markdown-key`);

              // Clear by question pattern
              const storageKey = `md-${message.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
              sessionStorage.removeItem(storageKey);

              // Clear by advice ID
              if (newAdvice?.id) {
                sessionStorage.removeItem(`advice-content-${newAdvice.id}`);
              }
              logger.debug(
                '[adviceStore:submitMessage] Deferred sessionStorage cleanup executed.'
              );
            }
          } catch (e) {
            console.error('Error clearing session storage (deferred):', e);
          }
        }, 0); // Defer execution
        // */

        // Create error advice object with correct error flags
        const errorAdviceData: Partial<ConversationAdvice> = {
          status: 'ERROR',
          advice:
            promptResponse?.response || 'Sorry, I cannot process this request.',
          response: promptResponse?.response || '',
          isStreaming: false,
          isError: true,
          urls: [],
          page_source: [],
          page_title: [],
          urls_ccq: [],
          page_source_ccq: [],
          page_title_ccq: [],
          texts_ccq: [],
          modifications_ccq: [],
          urls_ccq_modif: [],
          page_source_ccq_modif: [],
          page_title_ccq_modif: [],
          texts_ccq_modif: [],
          caselaw_reference: [],
          caselaw_titre: [],
          caselaw_lienURL: [],
          caselaw_nomsDesPartis: [],
          relevant_forms: {
            page_url: [],
            doc_url: [],
            filename: [],
            title: [],
            content: [],
            filled_by: [],
          },
        };

        // Update the advice using our updateAdvice action
        await adviceActions.updateAdvice(newAdvice.id, errorAdviceData);

        // Get the updated advices array after the update
        const updatedAdvicesArray = adviceState.advices.get();

        // Update global state with batch to ensure consistency
        batch(() => {
          // Update conversation with the FULL advices array
          const updatedConversation = {
            ...adviceState.activeConversation.get(),
            advices: updatedAdvicesArray,
          };

          // Update both states to ensure they're in sync
          adviceState.activeConversation.set(
            updatedConversation as unknown as ConversationResource
          );

          // Set other states
          adviceState.status.set('error');
          adviceState.isStreaming.set(false);
          adviceState.isChatLoading.set(false);
          adviceState.streamingAdviceId.set(null);
        });

        // Clean up
        adviceActions.setActiveController(null);
        queryClient.invalidateQueries({
          queryKey: ['conversations'],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: ['conversation', chatId],
          exact: true,
        });

        return;
      }



      console.log(
        '[adviceStore:sumbitMessage] getting response from getAdvice:'
      );
      const _t3 = performance.now();
      response = await adviceService.getAdvice(
        augmentedPrompt,
        message,
        chatId,
        lang,
        newController.signal,
        onChunk,
        session as Session,
        newAdvice.id
      );
      console.log(`[TIMING] Bedrock streaming total: ${(performance.now() - _t3).toFixed(0)}ms`);

      console.log(
        '[adviceStore:sumbitMessage] response from getAdvice:',
        response
      );

      try {
        // Update with final response - handle nested structure
        let updatedAdvice: ConversationAdvice;

        // First, check if we already have a properly formatted advice
        // in the current state that we can use as a base
        const currentAdvices = adviceState.advices.get();
        const existingAdvice = currentAdvices.find(
          (a: ConversationAdvice) => a.id === newAdvice.id
        );

        if (
          existingAdvice &&
          existingAdvice.advice &&
          ((existingAdvice.urls && existingAdvice.urls.length > 0) ||
            (existingAdvice.urls_ccq && existingAdvice.urls_ccq.length > 0))
        ) {
          updatedAdvice = {
            ...existingAdvice,
            status: 'COMPLETED' as const,
            isStreaming: false,
            isNew: true,
          };
        } else {
          // Check if response has a nested response object
          const responseData =
            response.response && typeof response.response === 'object'
              ? response.response
              : response;

          updatedAdvice = {
            ...response,
            id: newAdvice.id,
            question: message,
            status: 'COMPLETED' as const,

            // Extract advice content
            advice: responseData.advice || '',

            // Preserve full laws object structure
            laws: responseData.laws,

            // Extract metadata fields - handle both nested and flat structures
            urls: responseData.urls || [],
            page_source: responseData.page_source || [],
            page_title: responseData.page_title || [],
            urls_ccq: responseData.urls_ccq || [],
            page_source_ccq: responseData.page_source_ccq || [],
            page_title_ccq: responseData.page_title_ccq || [],
            texts_ccq: responseData.texts_ccq || [],
            modifications_ccq: responseData.modifications_ccq || [],
            urls_ccq_modif: responseData.urls_ccq_modif || [],
            page_source_ccq_modif: responseData.page_source_ccq_modif || [],
            page_title_ccq_modif: responseData.page_title_ccq_modif || [],
            texts_ccq_modif: responseData.texts_ccq_modif || [],
            caselaw_reference: responseData.caselaw_reference || [],
            caselaw_titre: responseData.caselaw_titre || [],
            caselaw_lienURL: responseData.caselaw_lienURL || [],
            caselaw_nomsDesPartis: responseData.caselaw_nomsDesPartis || [],
            caselaw_texte: responseData.caselaw_texte || [],
            relevant_forms: responseData.relevant_forms || [],
            isNew: true,
            isStreaming: false,
          };
        }

        await adviceActions.updateAdvice(newAdvice.id, updatedAdvice);

        // Update the conversation in a single batch update
        batch(() => {
          // Find and update the existing advice
          const currentAdvices = adviceState.advices.get();
          const finalAdvices = currentAdvices.map(
            (advice: ConversationAdvice) =>
              advice.id === newAdvice.id ? updatedAdvice : advice
          );

          const finalConversation = {
            ...updatedConversation,
            advices: finalAdvices,
          };

          adviceState.activeConversation.set(finalConversation);
          adviceState.advices.set(finalAdvices);
          adviceState.status.set('ready');
          adviceState.isStreaming.set(false);
          adviceState.isChatLoading.set(false);
          adviceState.streamingAdviceId.set(null);
        });

        // Clean up and update cache
        adviceActions.setActiveController(null);
        queryClient.invalidateQueries({
          queryKey: ['conversations'],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: ['conversation', chatId],
          exact: true,
        });
      } catch (error: any) {
        if (error.name === 'AbortError') {
          adviceActions.reset();
        } else {
          logger.warn('[adviceStore] Request failed:', error);
          adviceActions.failRequest(error);
        }
        adviceActions.setActiveController(null);
      }
    } catch (error) {
      console.error('[DEBUG] Error in response handling:', error);
      adviceActions.completeRequest();

      if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string' &&
        (error.message.includes('unauthorized') ||
          error.message.includes('authentication'))
      ) {
        adviceActions.setError(error as Error);
      }
    }
  },

  /**
   * Resets the advice store state to initial values
   */
  reset: () => {
    const currentController = adviceState.activeController.get();
    if (currentController instanceof AbortController) {
      currentController.abort('RESET_CHAT');
      adviceActions.setActiveController(null);
      adviceState.activeConversation.set(null);
      adviceState.advices.set([]);
      adviceState.currentChatId.set(null);
    }
    batch(() => {
      adviceState.set({
        ...initialState,
        activeController: null,
      });
      adviceState.activeConversation.set(null);
      adviceState.advices.set([]);
      adviceState.currentChatId.set(null);
      adviceState.isChatLoading.set(false);
      adviceState.isStreaming.set(false);
      adviceState.isFirstAdviceAnimationComplete.set(false);
    });

    sidebarActions.resetSidebarState();
  },

  /**
   * Resets the advice store state and aborts any active requests
   */
  resetWithAbort: () => {
    const controller = adviceState.activeController.get();
    if (controller instanceof AbortController) {
      controller.abort('RESET_CHAT');
      adviceActions.setActiveController(null);
    }
    batch(() => {
      adviceState.set(initialState);
      adviceState.isChatLoading.set(false);
      adviceState.isStreaming.set(false);
      adviceState.isFirstAdviceAnimationComplete.set(false);
    });

    sidebarActions.resetSidebarState();
  },

  /**
   * Fetches a conversation by ID
   * @param chatId - The ID of the conversation to fetch
   * @returns The fetched conversation
   */
  fetchConversation: async (chatId: string) => {
    const session = await adviceActions.ensureSession();
    adviceActions.startRequest();
    adviceActions.startLoading();
    const conversation = await adviceService.getById(
      chatId,
      session as Session
    );

    // Transform each advice to include flat arrays from sources
    if (conversation.advices && Array.isArray(conversation.advices)) {
      conversation.advices = conversation.advices.map((advice) =>
        adviceActions.transformSources(advice)
      );
    }

    // DEBUG: Citation persistence — log citationIndex after load
    if (conversation.advices && Array.isArray(conversation.advices)) {
      conversation.advices.forEach((advice: any, aIdx: number) => {
        console.log(`[DEBUG][fetchConversation] advice[${aIdx}] sources citationIndex after load:`,
          advice.sources?.map((s: any, i: number) => ({
            idx: i,
            type: s.type,
            citationIndex: s.citationIndex,
            pageTitle: s.pageTitle?.substring(0, 40),
          })) ?? 'no sources'
        );
      });
    }

    adviceActions.setCurrentChatId(chatId);
    adviceState.activeConversation.set(conversation);
    adviceState.advices.set(conversation.advices);
    adviceActions.completeRequest();
    return conversation;
  },

  /**
   * Marks an advice as seen if it is ready
   * @param adviceId - The ID of the advice to mark as seen
   * @returns Whether the advice was successfully marked as seen
   */
  markAdviceAsSeen: (adviceId: string) => {
    try {
      const advices = adviceState.advices.get();
      const index = advices.findIndex(
        (a: ConversationAdvice) => a.id === adviceId
      );

      if (index !== -1) {
        if (advices[index].status === 'COMPLETED') {
          adviceState.advices[index].isNew.set(false);

          if (!adviceState.isFirstAdviceAnimationComplete.get()) {
            adviceState.isFirstAdviceAnimationComplete.set(true);
          }

          return true;
        }
      }
    } catch (error) {
      console.error('[AdviceStore:markAdviceAsSeen] Error:', error);
    }

    return false;
  },

  /**
   * Updates the title of a conversation
   * @param id - The ID of the conversation
   * @param title - The new title
   * @returns The updated conversation
   */
  updateTitle: async (id: string, title: string) => {
    const session = await adviceActions.ensureSession();
    // Optimistically update observable state for conversation list
    const prevConvs = adviceState.conversations.get();
    const updatedItems = prevConvs.items.map((item) =>
      item.id === id ? { ...item, title } : item
    );
    adviceState.conversations.set({ items: updatedItems });
    if (adviceState.activeConversation.get()?.id === id) {
      const activeConv = adviceState.activeConversation.get();
      adviceState.activeConversation.set({
        ...(activeConv as unknown as ConversationResource),
        title,
      });
    }
    try {
      sidebarActions.startTransition();
      const updated = await adviceService.updateTitle(
        id,
        title,
        session as Session
      );

      // Ensure proper date handling
      const processedUpdate = {
        ...prevConvs.items.find((item) => item.id === id),
        ...updated,
        title: updated.title ?? title,
        createdAt: updated.createdAt ? new Date(updated.createdAt) : new Date(),
      };

      sidebarActions.completeTransition();
      const finalItems = adviceState.conversations
        .get()
        .items.map((item) => (item.id === id ? processedUpdate : item));

      batch(() => {
        adviceState.conversations.set({
          items: finalItems as ConversationResource[],
        });
      });

      return processedUpdate;
    } catch (error) {
      logger.error('[adviceStore] Failed to update title:', { id, error });
      adviceState.conversations.set(prevConvs);
      sidebarActions.setError(error as Error);
      throw error;
    }
  },

  /**
   * Forces a reactive update of the advice store
   */
  forceUpdate: () => {
    const currentStatus = adviceState.status.get();
    adviceState.status.set('submitted');
    setTimeout(() => {
      adviceState.status.set(currentStatus);
    }, 0);
  },

  /**
   * Sets whether the first advice animation is complete
   * @param isComplete - Whether the animation is complete
   */
  setFirstAdviceAnimationComplete: (isComplete: boolean) => {
    adviceState.isFirstAdviceAnimationComplete.set(isComplete);
  },

  /**
   * Resets the first advice animation state
   */
  resetFirstAdviceAnimationState: () => {
    adviceState.isFirstAdviceAnimationComplete.set(false);
  },

  /**
   * Toggles streaming mode
   * @param enabled - Whether streaming mode should be enabled
   */
  toggleStreamingMode: (enabled: boolean) => {
    adviceState.langFlowEnabled.set(enabled);
  },
};

// Selectors
export const adviceSelectors = {
  // Request state
  isPending: () => adviceState.status.get() === 'submitted',
  isStreaming: () => adviceState.status.get() === 'streaming',
  isReady: () => adviceState.status.get() === 'ready',
  isError: () => adviceState.status.get() === 'error',
  getError: () => adviceState.error.get(),
  islangFlowEnabled: () => adviceState.langFlowEnabled.get(),

  // Navigation state
  isTransitioning: () => adviceState.isTransitioning.get(),
  getCurrentChatId: () => adviceState.currentChatId.get(),

  // Conversation state
  getConversations: () => adviceState.conversations.get().items,
  getActiveConversation: () => adviceState.activeConversation.get(),
  getAdvices: () => adviceState.advices.get(),

  // Query state
  isLoadingConversations: () =>
    queryClient.isFetching({ queryKey: ['conversations'] }) > 0,
  getConversationsError: () => {
    const state = queryClient.getQueryState(['conversations']);
    return state?.error || null;
  },

  // Combined selectors
  getAllAdvices: () => {
    const state = adviceState.get();
    return state.advices;
  },
};

/**
 * Cleans and finalizes markdown text
 * @param str - The markdown string to clean
 * @returns The cleaned markdown string
 */
function finalizeMarkdown(str: string): string {
  if (!str) return str;

  let cleanedStr = str;

  if (cleanedStr.includes('\\n')) {
    cleanedStr = cleanedStr.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');
  }

  cleanedStr = cleanedStr
    .replace(/",?$/, '')
    .replace(/,"$/, '')
    .replace(/\.$",?$/, '.')
    .replace(/\}$/, '')
    .replace(/(?<!\[\d+)\]$/, '') // Only strip a trailing ] that is NOT part of a citation like [1]
    .trim();

  if (cleanedStr.endsWith('.,')) {
    cleanedStr = cleanedStr.slice(0, -2) + '.';
  }

  return cleanedStr;
}
